import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';

export interface WorkflowEventData {
  thought?: string;
  display_type?: string;
  truncated?: boolean;
  [key: string]: unknown;
}

export interface WorkflowEvent {
  id: string;
  run_id: string;
  node_instance_id: string | null;
  event_type: string;
  event_data: WorkflowEventData | null;
  timestamp: string;
  created_at: string;
}

interface GetRunEventsApiResponse {
  run_id: string;
  events: WorkflowEvent[];
  total: number;
}

/**
 * Fetches persisted execution events for a workflow run from the
 * ``GET .../runs/{run_id}/events`` endpoint.
 *
 * Fails open: any error (network, 4xx, 5xx) returns an empty array so
 * that missing CoT events never break the past-run view.
 *
 * @param orgId - Organisation ID
 * @param workflowId - Workflow ID
 * @param runId - Run ID
 * @param category - Event category filter: ``"chain_of_thought"`` (default),
 *   ``"actions"``, ``"events"``, or ``"all"``
 * @param limit - Maximum events to return (defaults to 500)
 */
export async function getRunEvents(
  orgId: string,
  workflowId: string,
  runId: string,
  category = 'chain_of_thought',
  limit = 500,
): Promise<WorkflowEvent[]> {
  try {
    const params = new URLSearchParams({
      category,
      limit: limit.toString(),
    });
    const response = await apiRequest<GetRunEventsApiResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/runs/${runId}/events?${params}`,
      target: 'workflow',
      method: 'GET',
    });

    if (response?.detail) {
      return [];
    }

    const data = (response.data ?? response) as GetRunEventsApiResponse;
    return data.events ?? [];
  } catch (error) {
    console.error('Error fetching run events:', error);
    Sentry.captureException(error, { extra: { context: 'get-run-events' } });
    return [];
  }
}
